import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TwinSetup } from "@/components/TwinSetup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Navigation, Check, UserRound, HelpCircle, Store, Users, Loader2 } from "lucide-react";
import { MarketplacesPanel } from "@/components/Marketplaces";
import { toast } from "sonner";

const Section = ({ title, icon: Icon, children, testid, description }) => (
  <section className="rounded-2xl border border-border bg-card p-5 md:p-6" data-testid={testid}>
    <div className="flex items-center gap-2.5 mb-1"><Icon className="h-5 w-5" /><h2 className="font-display text-xl">{title}</h2></div>
    {description && <p className="text-sm text-muted-foreground mb-4">{description}</p>}
    {children}
  </section>
);

const DemoSection = () => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.authStatus().then((s) => setOpen(!!s.demo)).catch(() => setOpen(false)); }, []);
  if (!user || user.is_demo) return null;
  const link = `${window.location.origin}/demo`;
  const rebuild = async () => {
    setBusy(true);
    try { const r = await api.demoReset(); setOpen(true); toast.success(`Demo closet ${open ? "rebuilt" : "created"} — ${r.copied.items} pieces copied`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build the demo"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-5">
      <Section title="Demo for friends & family" icon={Users} testid="settings-demo-section"
        description="A separate copy of your closet that anyone with the link can play with — add, style, sell — without touching yours. Rebuild it any time to start fresh.">
        {open === null ? <Skeleton className="h-10 w-64" /> : (
          <div className="flex flex-wrap items-center gap-3">
            {open && (
              <div className="flex items-center gap-2 rounded-full border border-border pl-4 pr-1.5 h-10 text-sm font-mono" data-testid="settings-demo-link">
                <span className="truncate max-w-[260px]">{link}</span>
                <Button size="sm" variant="secondary" className="rounded-full h-7" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="settings-demo-copy">Copy</Button>
              </div>
            )}
            <Button onClick={rebuild} disabled={busy} variant={open ? "outline" : "default"} className="rounded-full" data-testid="settings-demo-rebuild">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}{open ? "Rebuild from my closet" : "Create the demo"}
            </Button>
            {open && <p className="text-xs text-muted-foreground basis-full">Visitors can also type <b className="font-mono">demo</b> as the email on the login page.</p>}
          </div>
        )}
      </Section>
    </div>
  );
};

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [city, setCity] = useState("");
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    api.getSettings().then((s) => { setSettings(s); setCity(s.city || ""); }).catch(() => {});
  }, []);

  const saveCity = async () => {
    if (!city.trim()) return;
    try {
      const s = await api.updateSettings({ city: city.trim(), location_mode: "manual" });
      setSettings(s);
      toast.success(`Location set to ${s.location_label}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set city"); }
  };

  const useDevice = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const s = await api.updateSettings({ lat: pos.coords.latitude, lon: pos.coords.longitude, location_mode: "auto", location_label: "" });
          setSettings(s);
          toast.success(`Using your location: ${s.location_label}`);
        } catch { toast.error("Could not save location"); }
        finally { setLocating(false); }
      },
      () => { toast.error("Location permission denied"); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const toggleUnits = async (metric) => {
    try { const s = await api.updateSettings({ units: metric ? "metric" : "imperial" }); setSettings(s); }
    catch { toast.error("Could not update units"); }
  };

  if (!settings) return <Skeleton className="h-64 w-full max-w-xl rounded-[var(--radius)]" />;

  return (
    <div className="max-w-5xl">
      <p className="eyebrow">Settings</p>
      <h1 className="font-display text-4xl sm:text-5xl tracking-[-0.02em] mt-1">Make it yours.</h1>
      <p className="text-muted-foreground mt-2">Your twin, your weather, your look.</p>

      <div className="mt-8 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Section title="Location & weather" icon={MapPin} testid="settings-location" description="Outfits follow your forecast.">
          <div className="flex items-center gap-2 text-sm mb-4"><span className="meaning-dot bg-info" /><span>Current: <strong data-testid="settings-current-location">{settings.location_label || "Not set"}</strong></span></div>
          <Label className="text-sm">Enter a city</Label>
          <div className="mt-1.5 flex gap-2">
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Paris" data-testid="settings-manual-city-input" onKeyDown={(e) => e.key === "Enter" && saveCity()} />
            <Button onClick={saveCity} className="rounded-full" data-testid="settings-save-city-button"><Check className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-2 my-4"><div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or</span><div className="h-px flex-1 bg-border" /></div>
          <Button variant="secondary" onClick={useDevice} disabled={locating} className="w-full rounded-full" data-testid="settings-use-device-location-button">
            <Navigation className={`h-4 w-4 mr-1.5 ${locating ? "animate-pulse" : ""}`} /> {locating ? "Locating…" : "Use my device location"}
          </Button>
          <div className="mt-5 flex items-center justify-between border-t border-border pt-4" data-testid="settings-keep-listed">
            <div><p className="text-sm font-medium">Keep listed items in outfit suggestions</p><p className="text-xs text-muted-foreground">{settings.keep_listed_in_outfits === false ? "Off - pieces for sale are left out of generated outfits" : "On - you keep wearing pieces until they sell"}</p></div>
            <Switch checked={settings.keep_listed_in_outfits !== false} onCheckedChange={async (v) => { try { const s2 = await api.updateSettings({ keep_listed_in_outfits: v }); setSettings(s2); toast.success(v ? "Listed pieces stay in suggestions" : "Listed pieces left out of suggestions"); } catch { toast.error("Could not update"); } }} data-testid="settings-keep-listed-switch" />
          </div>
          <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
            <div><p className="text-sm font-medium">Temperature units</p><p className="text-xs text-muted-foreground">{settings.units === "metric" ? "Celsius (°C)" : "Fahrenheit (°F)"}</p></div>
            <div className="flex items-center gap-2 text-xs"><span>°F</span><Switch checked={settings.units === "metric"} onCheckedChange={toggleUnits} data-testid="settings-units-switch" /><span>°C</span></div>
          </div>
        </Section>

        <Section title="ThreadList" icon={HelpCircle} testid="settings-help" description="Re-run the setup: purpose, your details, style quiz and twin check.">
          <Button asChild variant="secondary" size="sm" className="rounded-full"><Link to="/welcome" data-testid="settings-restart-onboarding">Restart onboarding</Link></Button>
        </Section>
      </div>

      <DemoSection />

      <div className="mt-5" id="marketplaces">
        <Section title="Sell everywhere" icon={Store} testid="settings-marketplaces-section" description="Connect eBay (more coming). List any piece with one tap. When it sells you get the shipping label by email and your balance is ready to cash out.">
          <MarketplacesPanel />
        </Section>
      </div>

      <div className="mt-5">
        <Section title="Your twin" icon={UserRound} testid="settings-twin-section" description="Photos of you so every outfit renders on your own body, from every angle. If the twin doesn't look like you, add more photos and render again.">
          <TwinSetup />
        </Section>
      </div>
    </div>
  );
}
